import { createElement, useRef, useEffect, useState, useCallback, useMemo } from 'react';
import Viewer from 'bpmn-js/lib/Viewer';
import ModelingModule from 'bpmn-js/lib/features/modeling';
import MoveCanvasModule from 'diagram-js/lib/navigation/movecanvas';
import MinimapModule from 'diagram-js-minimap';
import CustomZoomScrollModule from './customZoomScroll/zoomScroll';
import customTranslate from './customTranslate/customTranslate';
import Pane from './pane';
import request from '@/utils/request';
import { append as svgAppend, attr as svgAttr, create as svgCreate } from 'tiny-svg';
import { useLocalObservable, observer } from 'mobx-react';
import { bpmnStore } from '@/store';
import './index.scss';
import 'diagram-js-minimap/assets/diagram-js-minimap.css';
const WorkFlowDiagram = observer((props) => {
    const { setTaskFlowVOS, setClickedElement } = useLocalObservable(() => bpmnStore);
    const canvasRef = useRef();
    const viewerRef = useRef({});
    const configRef = useRef({ activeActivityIds: [], highLightedFlows: [] });
    const [height, setHeight] = useState('calc(100% - 300px)');
    const customTranslateModule = useMemo(() => ({
        translate: ['value', customTranslate],
    }), []);
    const { __designMode, ctx: __ctx } = props;
    const isDesign = __designMode === 'design';
    const { procInstId, } = __ctx?.appHelper?.constants || {};
    const fixViewPort = () => {
        const canvas = viewerRef.current?.get('canvas');
        canvas.zoom('fit-viewport', 'auto');
    };
    const importXml = useCallback((xml) => {
        viewerRef.current.importXML(xml, () => {
            fixViewPort();
            markerElement();
            arrowColor();
        });
    }, []);
    const queryDiagram = useCallback(async () => {
        const { content: { workflowXml, activeActivityIds = [], highLightedFlows = [], taskFlowVOS = [] }, } = await request.get(`/api/process/workflowDiagram/information/${procInstId}`);
        importXml(workflowXml);
        configRef.current = {
            activeActivityIds,
            highLightedFlows,
        };
        setTaskFlowVOS({ taskFlowVOS, activeActivityIds, highLightedFlows });
    }, [importXml, procInstId, setTaskFlowVOS]);
    useEffect(() => {
        viewerRef.current = new Viewer({
            container: canvasRef.current,
            propertiesPanel: {
                parent: '#properties-panel',
            },
            additionalModules: [
                ModelingModule,
                MoveCanvasModule,
                CustomZoomScrollModule,
                customTranslateModule,
                MinimapModule,
            ],
        });
        if (isDesign) {
            viewerRef.current.importXML(xmlSample, () => {
                fixViewPort();
            });
            return;
        }
        queryDiagram();
        return () => {
            viewerRef.current?.destory();
        };
    }, [customTranslateModule, isDesign, queryDiagram]);
    useEffect(() => {
        const eventBus = viewerRef.current.get('eventBus');
        eventBus.on('element.click', (e) => {
            const { element } = e;
            setClickedElement(element);
        });
    }, [setClickedElement]);
    const onMounseMove = (height) => {
        setHeight(`calc(100% - ${height}px)`);
    };
    const markerElement = () => {
        const canvas = viewerRef.current?.get('canvas');
        configRef.current.activeActivityIds?.forEach((item) => {
            canvas.addMarker(item, 'nodeSuccess');
        });
        configRef.current.highLightedFlows?.forEach((item) => {
            canvas.addMarker(item, 'lineSuccess');
        });
    };
    const arrowColor = () => {
        const marker = svgCreate('marker');
        svgAttr(marker, {
            id: 'sequenceflow-arrow-green',
            viewBox: '0 0 20 20',
            refX: '11',
            refY: '10',
            markerWidth: '10',
            markerHeight: '10',
            orient: 'auto',
        });
        const path = svgCreate('path');
        svgAttr(path, {
            d: 'M 1 5 L 11 10 L 1 15 Z',
            style: ' stroke-width: 1px; stroke: #34eb5b;fill:#34eb5b; stroke-linecap: round; stroke-dasharray: 10000, 1; ',
        });
        const defs = document.querySelector('defs');
        svgAppend(marker, path);
        svgAppend(defs, marker);
    };
    return (createElement("div", { className: "bpmn-container" },
        createElement("div", { className: "canvas", id: "canvas", ref: canvasRef, style: { height } }),
        createElement(Pane, { onMounseMove: onMounseMove })));
});
export default WorkFlowDiagram;
const xmlSample = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:di="http://www.omg.org/spec/DD/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" xmlns:modeler="http://camunda.org/schema/modeler/1.0" id="Definitions_Start" targetNamespace="http://bpmn.io/schema/bpmn" exporter="Camunda Modeler" exporterVersion="4.12.0" modeler:executionPlatform="Camunda Platform" modeler:executionPlatformVersion="7.15.0">
  <bpmn:process id="Start" isExecutable="true">
    <bpmn:startEvent id="StartEvent">
      <bpmn:outgoing>Flow_1pod5oz</bpmn:outgoing>
    </bpmn:startEvent>
    <bpmn:userTask id="Activity_0mwrfam" name="节点一">
      <bpmn:incoming>Flow_1pod5oz</bpmn:incoming>
      <bpmn:outgoing>Flow_1oi83d1</bpmn:outgoing>
    </bpmn:userTask>
    <bpmn:sequenceFlow id="Flow_1pod5oz" sourceRef="StartEvent" targetRef="Activity_0mwrfam" />
    <bpmn:userTask id="Activity_1xj54cd" name="节点二">
      <bpmn:incoming>Flow_1oi83d1</bpmn:incoming>
      <bpmn:outgoing>Flow_0hvkgmd</bpmn:outgoing>
    </bpmn:userTask>
    <bpmn:sequenceFlow id="Flow_1oi83d1" sourceRef="Activity_0mwrfam" targetRef="Activity_1xj54cd" />
    <bpmn:endEvent id="Event_08f047h">
      <bpmn:incoming>Flow_0hvkgmd</bpmn:incoming>
    </bpmn:endEvent>
    <bpmn:sequenceFlow id="Flow_0hvkgmd" sourceRef="Activity_1xj54cd" targetRef="Event_08f047h" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Start">
      <bpmndi:BPMNEdge id="Flow_1pod5oz_di" bpmnElement="Flow_1pod5oz">
        <di:waypoint x="648" y="350" />
        <di:waypoint x="700" y="350" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNEdge id="Flow_1oi83d1_di" bpmnElement="Flow_1oi83d1">
        <di:waypoint x="800" y="350" />
        <di:waypoint x="860" y="350" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNEdge id="Flow_0hvkgmd_di" bpmnElement="Flow_0hvkgmd">
        <di:waypoint x="960" y="350" />
        <di:waypoint x="1022" y="350" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent">
        <dc:Bounds x="612" y="332" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Activity_0mwrfam_di" bpmnElement="Activity_0mwrfam">
        <dc:Bounds x="700" y="310" width="100" height="80" />
        <bpmndi:BPMNLabel />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Activity_1xj54cd_di" bpmnElement="Activity_1xj54cd">
        <dc:Bounds x="860" y="310" width="100" height="80" />
        <bpmndi:BPMNLabel />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Event_08f047h_di" bpmnElement="Event_08f047h">
        <dc:Bounds x="1022" y="332" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>

`;
